import { useRef, useLayoutEffect } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

/**
 * Check if user prefers reduced motion
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

interface ParallaxLayer {
  /** CSS selector for the layer */
  selector: string
  /** Speed multiplier (0.1 = slow, 1 = normal, 2 = fast) */
  speed: number
}

interface ParallaxOptions {
  /** Start position for ScrollTrigger */
  start?: string
  /** End position for ScrollTrigger */
  end?: string
}

/**
 * Multi-layer parallax effect
 * Creates depth by moving layers at different speeds during scroll
 *
 * @example
 * const sectionRef = useParallaxLayers({
 *   background: { selector: '.bg-image', speed: 0.3 },
 *   midground: { selector: '.floating-elements', speed: 0.6 },
 *   foreground: { selector: '.content', speed: 1.2 }
 * })
 */
export function useParallaxLayers<T extends HTMLElement>(
  layers: {
    background?: ParallaxLayer
    midground?: ParallaxLayer
    foreground?: ParallaxLayer
  },
  options?: ParallaxOptions
) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      const allLayers = [layers.background, layers.midground, layers.foreground].filter(Boolean)

      allLayers.forEach((layer) => {
        if (!layer) return

        const elements = ref.current!.querySelectorAll(layer.selector)
        if (elements.length === 0) return

        // Calculate movement based on speed
        // Lower speed = element moves less = appears further away
        // Higher speed = element moves more = appears closer
        const yPercent = (1 - layer.speed) * 50

        gsap.to(elements, {
          yPercent,
          ease: 'none',
          scrollTrigger: {
            trigger: ref.current,
            start: options?.start ?? 'top bottom',
            end: options?.end ?? 'bottom top',
            scrub: true,
          },
        })
      })
    }, ref)

    return () => ctx.revert()
  }, [layers, options?.start, options?.end])

  return ref
}

/**
 * Simple parallax for a single element
 *
 * @example
 * const imageRef = useParallax(0.3) // Slow parallax for background image
 */
export function useParallax<T extends HTMLElement>(speed = 0.5, options?: ParallaxOptions) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const yPercent = (1 - speed) * 30

    const ctx = gsap.context(() => {
      gsap.to(ref.current, {
        yPercent,
        ease: 'none',
        scrollTrigger: {
          trigger: ref.current,
          start: options?.start ?? 'top bottom',
          end: options?.end ?? 'bottom top',
          scrub: true,
        },
      })
    }, ref)

    return () => ctx.revert()
  }, [speed, options?.start, options?.end])

  return ref
}

/**
 * Horizontal parallax - element moves sideways during scroll
 */
export function useHorizontalParallax<T extends HTMLElement>(
  speed = 0.3,
  direction: 'left' | 'right' = 'left',
  options?: ParallaxOptions
) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const xPercent = direction === 'left' ? -speed * 20 : speed * 20

    const ctx = gsap.context(() => {
      gsap.to(ref.current, {
        xPercent,
        ease: 'none',
        scrollTrigger: {
          trigger: ref.current,
          start: options?.start ?? 'top bottom',
          end: options?.end ?? 'bottom top',
          scrub: true,
        },
      })
    }, ref)

    return () => ctx.revert()
  }, [speed, direction, options?.start, options?.end])

  return ref
}

/**
 * Scale parallax - element scales up/down during scroll
 */
export function useScaleParallax<T extends HTMLElement>(startScale = 0.9, endScale = 1.1, options?: ParallaxOptions) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      gsap.fromTo(
        ref.current,
        { scale: startScale },
        {
          scale: endScale,
          ease: 'none',
          scrollTrigger: {
            trigger: ref.current,
            start: options?.start ?? 'top bottom',
            end: options?.end ?? 'bottom top',
            scrub: true,
          },
        }
      )
    }, ref)

    return () => ctx.revert()
  }, [startScale, endScale, options?.start, options?.end])

  return ref
}

/**
 * Rotation parallax - element rotates during scroll
 */
export function useRotationParallax<T extends HTMLElement>(
  startRotation = -5,
  endRotation = 5,
  options?: ParallaxOptions
) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      gsap.fromTo(
        ref.current,
        { rotation: startRotation },
        {
          rotation: endRotation,
          ease: 'none',
          scrollTrigger: {
            trigger: ref.current,
            start: options?.start ?? 'top bottom',
            end: options?.end ?? 'bottom top',
            scrub: true,
          },
        }
      )
    }, ref)

    return () => ctx.revert()
  }, [startRotation, endRotation, options?.start, options?.end])

  return ref
}
